import { Coordinates } from "@optimusride/client-library/@types/grpc/common";
import dayjs from "dayjs";

export const convertArrayToObject = <T>(arr: string[], defaultValue: T) => arr.reduce(
    (curr: { [keys: string]: T }, key) => ({
        ...curr,
        [key]: defaultValue,
    }),
    {},
);

export const get12HourTime = (date: Date, tz?: string) => dayjs.tz(date, tz).format("h:mm A");

export const getDateString = (date: Date, tz?: string) => dayjs.tz(date, tz).format("MMMM D, YYYY");

export const getInitials = (text: string) => text
    .split(" ")
    .map((name) => name.charAt(0))
    .join("")
    .toUpperCase();

export const convertLatLng = (latLng: Coordinates): google.maps.LatLngLiteral => ({
    lat: latLng.latitude,
    lng: latLng.longitude,
});

// Ported from https://en.wikipedia.org/wiki/Haversine_formula#:~:text=The%20haversine%20formula%20determines%20the,and%20angles%20of%20spherical%20triangles.
// and http://www.movable-type.co.uk/scripts/latlong.html
/**
 * Gets the distance between two lat lon objects in miles.
 * @param {Coordinates} latLngA - First lat lon
 * @param {CoordinatesCoordinates} latLngB - Second lat long
 * @returns {number} distance in miles
 */
export const distanceBetweenLatLng = (latLngA: Coordinates, latLngB: Coordinates) => {
    const R = 6371e3; // metres
    const φ1 = (latLngA.latitude * Math.PI) / 180; // φ, λ in radians
    const φ2 = (latLngB.latitude * Math.PI) / 180;
    const Δφ = ((latLngB.latitude - latLngA.latitude) * Math.PI) / 180;
    const Δλ = ((latLngB.longitude - latLngA.longitude) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2)
            + Math.cos(φ1) * Math.cos(φ2)
            * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const d = R * c; // in metres

    return d * 0.000621371; // converts meters to miles
};

/**
 * Converts number to letter (1 = A, 2 = B,...27 = AA, 28 = AB...)
 * @param id number to convert to letter
 * @returns letter in the form of A-Z, AA, AB, etc
 */
export const numberToLetter = (num: number): string => {
    const mod = num % 26;
    // eslint-disable-next-line no-bitwise
    let pow = num / 26 | 0;
    // eslint-disable-next-line no-plusplus
    const out = mod ? String.fromCharCode(64 + mod) : (--pow, "Z");
    return pow ? numberToLetter(pow) + out : out;
};

const NUMBER_REGEX = /^(\d)+$/gm;
/**
 * Checks if a string (ie: "123") is an integer or not
 * @param num string ("123", "abcd")
 * @returns boolean if the value is an integer or not
 */
export const isStringInteger = (num: string): boolean => NUMBER_REGEX.test(num);

/**
 * converts radians to degrees
 * @param radians number
 * @returns degree
 */
export const radiansToDegree = (radians: number) => radians * (180 / Math.PI);

/**
 * Gets query param from url string, to be used outside of hooks
 * @param qp queryParam to get
 * @returns value
 */
export const getQueryParam = (qp: string) => (
    new URL(window.location.toString())
).searchParams.get(qp);
